import React from "react";
import { Reveal } from "./useReveal";
import {
  IconPlay,
  IconHint,
  IconAnalyze,
  IconCourse,
  IconCommunity,
} from "./TileMotif";

const features = [
  {
    id: "play",
    num: "01",
    title: "Play vs AI Opponents",
    desc: "Real Hong Kong Cantonese Mahjong against three AI players who scale to your skill.",
    Icon: IconPlay,
    span: "md:col-span-7 md:row-span-2",
    size: "lg",
  },
  {
    id: "hints",
    num: "02",
    title: "Real‑Time AI Hints",
    desc: "Tap for in‑game suggestions on what to discard — and why.",
    Icon: IconHint,
    span: "md:col-span-5",
    size: "md",
  },
  {
    id: "analysis",
    num: "03",
    title: "AI Post‑Game Analysis",
    desc: "A full coaching breakdown of every hand after the game ends.",
    Icon: IconAnalyze,
    span: "md:col-span-5",
    size: "md",
  },
  {
    id: "courses",
    num: "04",
    title: "Step‑by‑Step Courses",
    desc: "From rules and etiquette to scoring, fan combinations, and strategy.",
    Icon: IconCourse,
    span: "md:col-span-6",
    size: "md",
  },
  {
    id: "community",
    num: "05",
    title: "Community Events",
    desc: "A directory of local clubs, tournaments, and meetups worth joining.",
    Icon: IconCommunity,
    span: "md:col-span-6",
    size: "md",
  },
];

const FeatureCard = ({ f, i }) => {
  const isLead = f.size === "lg";
  return (
    <Reveal
      delay={i * 80}
      className={`${f.span} group relative bg-[#F5F0E8] border border-[#E5DFD3] hover:border-[#3D7A5A] rounded-sm transition-colors duration-300`}
    >
      <article
        data-testid={`feature-card-${f.id}`}
        className={`h-full flex flex-col ${isLead ? "p-7 md:p-10" : "p-6 md:p-8"}`}
      >
        <div className="flex items-start justify-between mb-5 md:mb-6">
          <f.Icon size={isLead ? 56 : 44} />
          <span className="font-han text-[11px] tracking-[0.2em] text-[#3D7A5A]">
            {f.num}
          </span>
        </div>

        <h3
          className={`${
            isLead ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"
          } leading-tight tracking-tight text-[#1A1A1A]`}
        >
          <span className="font-black">{f.title.split(" ")[0]}</span>{" "}
          <span className="font-light">
            {f.title.split(" ").slice(1).join(" ")}
          </span>
        </h3>

        <p
          className={`${
            isLead ? "mt-5 text-base md:text-lg max-w-md" : "mt-4 text-sm md:text-[15px]"
          } text-[#3a3a38] leading-relaxed`}
        >
          {f.desc}
        </p>

        {isLead && (
          <div className="mt-auto pt-8 flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-[#3D7A5A] font-bold">
            <span className="inline-block w-8 h-px bg-[#3D7A5A]" />
            Core gameplay
          </div>
        )}
      </article>
      <span
        aria-hidden="true"
        className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#3D7A5A] group-hover:w-full transition-[width] duration-500"
      />
    </Reveal>
  );
};

const Features = () => {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="bg-[#F5F0E8] py-20 md:py-32 border-t border-[#E5DFD3]"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="md:grid md:grid-cols-12 md:gap-10 mb-12 md:mb-16">
          <div className="md:col-span-4">
            <p className="text-[11px] md:text-xs uppercase tracking-[0.25em] text-[#3D7A5A] mb-4">
              Features
            </p>
          </div>
          <div className="md:col-span-8">
            <h2
              data-testid="features-headline"
              className="text-3xl sm:text-4xl md:text-[48px] leading-[1.05] tracking-tight text-[#1A1A1A]"
            >
              <span className="font-black">Everything you need</span>{" "}
              <span className="font-light">to go from curious to confident.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 auto-rows-fr">
          {features.map((f, i) => (
            <FeatureCard key={f.id} f={f} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
